import React from 'react'
import {
  useAvailableIntegrations,
  useGetIntegrationStatus
} from '@/application/queries/use-integrations'
import { IntegrationCard } from '@/ui/components/integration-components/integration-card'
import { useAppAbout } from './use-app-about'

interface IntegrationsListProps {
  category: string
  searchQuery: string
  // On top of the category: Apps keeps every category but platforms and carriers.
  filter?: (tags: string[]) => boolean
}

export const IntegrationsList: React.FC<IntegrationsListProps> = ({
  category,
  searchQuery,
  filter
}) => {
  const { data: integrations, status } = useAvailableIntegrations()

  const { integrationStatusData } = useGetIntegrationStatus()
  const about = useAppAbout()

  if (status === 'pending') return <div>Loading...</div>

  if (status === 'error') {
    return <div>Error loading integrations.</div>
  }

  const filteredIntegrations = (integrations ?? []).filter((intg) => {
    return (
      (category !== 'All'
        ? intg.integration_category_tags?.includes(
            category.toLowerCase().replaceAll(' ', '_')
          )
        : true) &&
      (!filter || filter(intg.integration_category_tags ?? [])) &&
      intg.integration_name
        ?.toLowerCase()
        .replace(/_/g, ' ')
        .includes(searchQuery.toLocaleLowerCase())
    )
  })

  return (
    <div className="p-0">
      {filteredIntegrations.length > 0 && (
        <div className="mb-20 mt-4 grid grid-cols-[repeat(auto-fill,minmax(200px,1fr))] gap-2">
          {filteredIntegrations.map(
            ({
              integration_id,
              integration_name,
              integration_logo,
              integration_description,
              integration_category_tags
            }) => {
              return (
                <IntegrationCard
                  key={String(integration_id ?? '')}
                  id={String(integration_id ?? '')}
                  name={integration_name ?? 'Unknown Name'}
                  description={about({
                    appKey: integration_name ?? '',
                    part: 'summary',
                    fallback:
                      integration_description ?? 'No description available'
                  })}
                  imageSrc={integration_logo ?? ''}
                  status={
                    integrationStatusData?.find(
                      (integrationStatus) =>
                        integrationStatus.name === integration_name
                    )?.status
                  }
                  tags={
                    Array.isArray(integration_category_tags)
                      ? integration_category_tags
                      : []
                  }
                  category={integration_category_tags?.[0] ?? ''}
                />
              )
            }
          )}
        </div>
      )}
      {filteredIntegrations.length === 0 && (
        <div className="flex size-full items-center justify-center">
          <p className="text-sm text-slate-400">No integrations found</p>
        </div>
      )}
    </div>
  )
}
